import { FILE_TYPES } from '@kirin/api-client/contracts';
import type { AlertSourceDetails } from '@kirin/api-client/contracts/alerts';

/**
 * The delimiter `formatToolLocation` (backend) inserts between an MCP tool's
 * `Tool: "<name>" (Server: <server>)` identity line and the head-truncated
 * description preview it appends.
 */
const TOOL_DESCRIPTION_MARKER = '\n\nTool description:';

/** The `mcp-tool-response:` namespace the agent stamps on tool-response scan paths (KIR-3157). */
const MCP_TOOL_RESPONSE_PREFIX = `${FILE_TYPES.MCP_TOOL_RESPONSE}:`;

/** The literal the hidden-chars scanner (policies/hidden/index.ts) puts before the path. */
const HIDDEN_FILE_MARKER = 'File: ';

/**
 * KIR-4100: display-only strip of the synthetic `mcp-tool-response:` namespace
 * (KIR-3157) from a finding `location`. The prefix is START-anchored — bare, or
 * just after the hidden-chars `File: ` marker — so an attacker-controlled
 * `Message: "…"` payload (always after the first newline) is never touched.
 * Never mutates the stored location/finding_key; mirrors
 * {@link trimMcpToolLocationPreview} + `getFilePathDisplayLabel`. Returns the
 * input unchanged when no anchored prefix is present, null for null/undefined.
 */
export function stripMcpToolResponseLocationPrefix(
  location: string | null | undefined
): string | null {
  if (location == null) return null;
  // Bare sentinel: `mcp-tool-response:<server>:<tool>` or a resolved path.
  if (location.startsWith(MCP_TOOL_RESPONSE_PREFIX)) {
    const rest = location.slice(MCP_TOOL_RESPONSE_PREFIX.length);
    return rest === '' ? location : rest;
  }
  // Hidden-chars wrapper: `File: mcp-tool-response:<path>, Positions: …`.
  const wrapped = HIDDEN_FILE_MARKER + MCP_TOOL_RESPONSE_PREFIX;
  if (location.startsWith(wrapped)) {
    const rest = location.slice(wrapped.length);
    return rest === '' ? location : HIDDEN_FILE_MARKER + rest;
  }
  return location;
}

/**
 * KIR-4175: for an MCP tool-description alert whose detection evidence is shown
 * elsewhere (the "More details" panel), drop the misleading head-truncated
 * description preview from the rendered `location`, leaving just the stable
 * `Tool: "<name>" (Server: <server>)` identity.
 *
 * Display-only: callers pass the raw `location` and render the result. The
 * stored/hashed `location` (and therefore the finding_key) is never touched.
 * Returns the input unchanged when there is no preview to trim.
 */
export function trimMcpToolLocationPreview(
  location: string | null | undefined
): string | null {
  if (location == null) return null;
  return location.split(TOOL_DESCRIPTION_MARKER)[0];
}

/** The literal `policies/hidden/index.ts` puts before the decoded payload. */
const HIDDEN_MESSAGE_MARKER = '\nMessage: "';

/** A payload line closed by its quote — what the resolver needs to render the row. */
const CLOSED_HIDDEN_MESSAGE = /["“”]\s*$/;

/**
 * KIR-4229: drop the decoded-payload line from a hidden-character `location`
 * when Detection Logic renders that payload on its own row. A steganographic
 * payload runs to hundreds of characters, and printed twice it buries the
 * Location's job of naming the file and offsets.
 *
 * The payload is always appended last, so everything from the scanner's marker
 * onward goes; a payload embedding that marker cannot smuggle a truncation past
 * the real one, since the first occurrence is the scanner's.
 *
 * Display-only — the stored value and its finding_key are untouched. Unchanged
 * when there is no payload line, or when it is not closed by its quote: the
 * resolver skips the row for a malformed payload too, and trimming here as well
 * would erase it from the card.
 */
export function trimHiddenMessageLocationSuffix(
  location: string | null | undefined
): string | null {
  if (location == null) return null;
  const markerAt = location.indexOf(HIDDEN_MESSAGE_MARKER);
  if (markerAt === -1) return location;
  const payload = location.slice(markerAt + HIDDEN_MESSAGE_MARKER.length);
  if (!CLOSED_HIDDEN_MESSAGE.test(payload)) return location;
  return location.slice(0, markerAt);
}

/**
 * Whether an alert/occurrence carries KIR-4175 MCP detection evidence — i.e. the
 * "More details" panel will render, so trimming the location preview is safe
 * (the full description is available there). False for non-MCP or non-regex
 * findings, and when the `alert_source_details` flag is off (no `sourceDetails`).
 */
export function hasMcpDetectionEvidence(
  sourceDetails: AlertSourceDetails | undefined
): boolean {
  return sourceDetails?.kind === 'mcp' && sourceDetails.matchedText != null;
}

/** The `Tool: "x" (Server: y)` identity plus the match window, for the Location block. */
export interface McpLocationEvidence {
  identity: string | null;
  before: string;
  match: string;
  after: string;
}

/**
 * KIR-4175: build the Location display for an MCP tool-description alert — the
 * stable tool/server identity plus the description **window around the match**
 * (rendered `before <mark>match</mark> after`) instead of the head-truncated
 * preview. Returns `null` when the finding has no match evidence, so the caller
 * falls back to the raw location. Display-only; never touches the stored value.
 */
export function getMcpLocationEvidence(
  sourceDetails: AlertSourceDetails | undefined,
  location: string | null | undefined
): McpLocationEvidence | null {
  if (sourceDetails?.kind !== 'mcp' || sourceDetails.matchedText == null) {
    return null;
  }
  return {
    identity: trimMcpToolLocationPreview(location),
    before: sourceDetails.matchedContextBefore ?? '',
    match: sourceDetails.matchedText,
    after: sourceDetails.matchedContextAfter ?? '',
  };
}
